/**
 * Сущность врага в игре
 * Реализует базовую логику врага: здоровье, движение, получение урона
 */
import { ISceneAdapter } from '../engine/ISceneAdapter';
import { EventBus } from '../core/EventBus';
import { createLogger } from '../utils/Logger';
import { isBossLookingPayload, resolveBossEncounterMetadata } from '../systems/BossEventPayloads';

// Интерфейс для компонента здоровья
interface HealthComponent {
  hp: number;
  maxHp: number;
}

/**
 * Класс сущности врага
 * Использует адаптер сцены для взаимодействия с Phaser
 */
export class EnemyEntity {
  private health: HealthComponent;
  public speed: number;
  private eventBus: EventBus;
  private physicsBody: any; // Заглушка для физического тела
  private readonly baseSpeed: number;
  private readonly logger = createLogger('EnemyEntity');
  private statusState: {
    burn?: { damagePerSecond: number; expiresAt: number };
    slow?: { multiplier: number; expiresAt: number };
  } = {};


  /**
   * Создает новую сущность врага
   * @param sceneAdapter Адаптер сцены для взаимодействия с Phaser
   * @param x Координата X для позиционирования
   * @param y Координата Y для позиционирования
   * @param texture Текстура врага
   * @param maxHp Максимальное здоровье врага
   * @param speed Скорость передвижения врага
   */
  constructor(
    sceneAdapter: ISceneAdapter,
    x: number,
    y: number,
    textureOrSprite: string | any,
    maxHp: number,
    speed: number
  ) {
    // Инициализируем компонент здоровья
    this.health = {
      hp: maxHp,
      maxHp: maxHp
    };

    // Устанавливаем скорость врага
    this.speed = speed;
    this.baseSpeed = speed;

    // Инициализируем EventBus
    this.eventBus = EventBus.getInstance();

    // Создаем спрайт или используем существующий
    if (typeof textureOrSprite === 'string') {
      this.sprite = sceneAdapter.createSprite(x, y, textureOrSprite);
    } else {
      this.sprite = textureOrSprite;
      // If sprite is passed, ensure it's active and visible (resetting from pool)
      if (this.sprite.setActive) this.sprite.setActive(true);
      if (this.sprite.setVisible) this.sprite.setVisible(true);
      this.sprite.setPosition(x, y);
    }

    // Sync health to sprite data for CollisionSystem
    this.sprite.setData('health', maxHp);

    // Создаем физическое тело через адаптер (или используем существующее)
    if (!this.sprite.body) {
      this.physicsBody = sceneAdapter.addPhysics(this.sprite);
    } else {
      this.physicsBody = this.sprite.body;
    }

    // Устанавливаем физические свойства
    if (this.physicsBody && typeof this.physicsBody.setCollideWorldBounds === 'function') {
      this.physicsBody.setCollideWorldBounds(true);
    }
    if (this.physicsBody && typeof this.physicsBody.setBounce === 'function') {
      this.physicsBody.setBounce(1);
    }
  }

  /**
   * Наносит урон врагу
   * @param amount Количество урона
   * @returns Оставшееся здоровье
   */
  public takeDamage(amount: number): number {
    this.health.hp -= amount;

    // Убедимся, что здоровье не становится отрицательным
    if (this.health.hp < 0) {
      this.health.hp = 0;
    }

    // Публикуем событие о получении урона
    this.eventBus.publish({
      type: 'enemy:damaged',
      data: {
        enemy: this,
        damage: amount,
        remainingHp: this.health.hp
      }
    });

    return this.health.hp;
  }

  /**
   * Проверяет, мертв ли враг
   * @returns true, если враг мертв (здоровье <= 0), иначе false
   */
  public isDead(): boolean {
    return this.health.hp <= 0;
  }

  /**
   * Возвращает текущее здоровье врага
   */
  public getHealth(): number {
    return this.health.hp;
  }

  /**
   * Возвращает максимальное здоровье врага
   */
  public getMaxHealth(): number {
    return this.health.maxHp;
  }

  /**
   * Восстанавливает здоровье врага
   * @param amount Количество здоровья для восстановления
   */
  public heal(amount: number): void {
    this.health.hp += amount;

    // Убедимся, что здоровье не превышает максимум
    if (this.health.hp > this.health.maxHp) {
      this.health.hp = this.health.maxHp;
    }
  }

  /**
   * Устанавливает направление движения врага к игроку
   * @param targetX Координата X цели (обычно игрока)
   * @param targetY Координата Y цели (обычно игрока)
   */
  // AI Component
  private ai: import('../systems/ai/EnemyAI').IEnemyAI | null = null;
  private sprite: any; // Keep reference to sprite

  /**
   * Устанавливает ИИ для врага
   */
  public setAI(ai: import('../systems/ai/EnemyAI').IEnemyAI): void {
    this.ai = ai;
  }

  /**
   * Обновление логики врага (вызывается каждый кадр)
   */
  public update(dt: number, player: { x: number, y: number }): void {
    if (this.isDead()) return;

    this.updateStatusEffects(dt);
    if (this.isDead()) return;

    if (this.ai) {
      this.ai.update(this, player, dt);
    } else {
      // Fallback or legacy movement if no AI
      this.moveToTarget(player.x, player.y);
    }
  }

  // Getters for AI compatibility
  public get x(): number {
    return this.sprite ? this.sprite.x : 0;
  }

  /**
   * Устанавливает направление движения врага к игроку
   * @param targetX Координата X цели (обычно игрока)
   * @param targetY Координата Y цели (обычно игрока)
   */
  public moveToTarget(_targetX: number, _targetY: number): void {
    // Legacy/Fallback implementation
    if (this.sprite && this.physicsBody) {
      const dx = _targetX - this.sprite.x;
      const dy = _targetY - this.sprite.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance > 5) {
        const vx = (dx / distance) * this.speed;
        const vy = (dy / distance) * this.speed;
        this.physicsBody.setVelocity(vx, vy);
      } else {
        this.physicsBody.setVelocity(0, 0);
      }
    }
  }

  public get y(): number {
    return this.sprite ? this.sprite.y : 0;
  }

  public getSprite(): any {
    return this.sprite;
  }

  public applyStatusEffects(effects: {
    burn?: { damagePerSecond: number; expiresAt: number };
    slow?: { multiplier: number; expiresAt: number };
  }): void {
    if (effects.burn) {
      this.statusState.burn = effects.burn;
      this.sprite.setData('burn', effects.burn);
    }

    if (effects.slow) {
      this.statusState.slow = effects.slow;
      this.sprite.setData('slow', effects.slow);
    }
  }

  private updateStatusEffects(dt: number): void {
    const now = this.sprite?.scene?.time?.now ?? Date.now();

    if (this.statusState.burn) {
      if (now >= this.statusState.burn.expiresAt) {
        this.statusState.burn = undefined;
        this.sprite.setData('burn', undefined);
      } else {
        const dotDamage = this.statusState.burn.damagePerSecond * dt;
        this.takeDamage(dotDamage);
        if (this.isDead()) {
          this.resolveStatusDeath('burn');
          return;
        }
      }
    }

    if (this.statusState.slow) {
      if (now >= this.statusState.slow.expiresAt) {
        this.statusState.slow = undefined;
        this.sprite.setData('slow', undefined);
        this.speed = this.baseSpeed;
      } else {
        this.speed = this.baseSpeed * this.statusState.slow.multiplier;
      }
    } else {
      this.speed = this.baseSpeed;
    }

    this.sprite.setData('health', this.health.hp);
  }

  private resolveStatusDeath(_source: 'burn'): void {
    const enemyId = this.sprite.getData('id');
    const xpReward = this.sprite.getData('xpReward');
    const bossEncounter = resolveBossEncounterMetadata(this.sprite, this.logger);
    if (!bossEncounter && isBossLookingPayload({ id: enemyId, enemy: this.sprite })) {
      this.logger.warn('status:death-missing-boss-encounter-metadata', { enemyId });
    }
    this.sprite.setData('health', this.health.hp);
    this.statusState.burn = undefined;
    this.statusState.slow = undefined;
    this.sprite.setData('burn', undefined);
    this.sprite.setData('slow', undefined);
    this.speed = this.baseSpeed;

    this.eventBus.publish({
      type: 'enemy:killed',
      data: {
        enemy: this.sprite,
        x: this.sprite.x,
        y: this.sprite.y,
        id: enemyId,
        xpValue: xpReward,
        ...(bossEncounter ? { bossEncounter } : {})
      }
    });

    this.sprite.getData('visualCleanup')?.();
    this.sprite.getData('characterVisual')?.reset();
    this.sprite.setActive?.(false);
    this.sprite.setVisible?.(false);
    this.physicsBody?.stop?.();
    if (this.physicsBody) this.physicsBody.enable = false;
  }
}

export default EnemyEntity;
